import { useState, useCallback } from 'react'
import useSecureFileUpload from 'pages/Nobo/components/useSecureFileUpload'
import { PRIVATE_S3_BUCKET_TYPE } from 'constants/fileExtensions'

// "user/<id>/files/1751563225_test_file.png" => "test_file.png"
const formatFileName = filePath => {
  const fileName = filePath.substring(filePath.lastIndexOf('/') + 1) // Get string after the last '/'
  return fileName.substring(fileName.indexOf('_') + 1) // Remove timestamp prefix if exists
}

/**
 * Shared hook for handling file upload functionality in request forms
 * @param {Array} initialFiles - initial file paths (for editing)
 * @param {function} getUrlFieldValues - function to get the value of the urls field
 * @param {function} setUrlFieldValue - function to set the value of the urls field
 * @returns {object} file upload state and handlers
 */
function useRequestFormFileUpload({
  initialFiles = [],
  getUrlFieldValues = null,
  setUrlFieldValue = null,
}) {
  const [filesToUpload, setFilesToUpload] = useState(() => {
    // Convert initial file paths to file objects for display
    return initialFiles.map(filePath => ({
      file: {
        name: formatFileName(filePath),
        url: filePath,
      },
      uploaded: true,
    }))
  })

  const handleUploadSuccess = useCallback(
    file => {
      // Truncate CDN URL to partial path format for compatibility
      const url = new URL(file.url)
      const truncatedUrl = decodeURIComponent(url.pathname.slice(1))

      // Update form URLs if getter/setter are provided
      if (getUrlFieldValues && setUrlFieldValue) {
        const currentUrls = getUrlFieldValues() || []
        setUrlFieldValue([...currentUrls, truncatedUrl])
      }

      // Mark the file as uploaded
      setFilesToUpload(prev =>
        prev.map(item => (item.file.name === file.fileName ? { ...item, uploaded: true } : item)),
      )
    },
    [getUrlFieldValues, setUrlFieldValue],
  )

  const secureFileUpload = useSecureFileUpload({
    onFileUploadSuccess: handleUploadSuccess,
    bucketType: PRIVATE_S3_BUCKET_TYPE,
  })

  const handleFileSelect = useCallback(
    ({ target: { files } }) => {
      const fileArray = Array.from(files)
      setFilesToUpload(prev => [...prev, ...fileArray.map(file => ({ file, uploaded: false }))])
      secureFileUpload.uploadFile(fileArray)
    },
    [secureFileUpload],
  )

  const handleFileRemove = useCallback(
    file => {
      setFilesToUpload(prev => {
        // Find index of the removed file if it was uploaded
        const removedIndex = prev.findIndex(item => item.file.name === file.name && item.uploaded)

        // Update form URLs if needed and getter/setter are provided
        if (removedIndex !== -1 && getUrlFieldValues && setUrlFieldValue) {
          const currentUrls = getUrlFieldValues() || []
          const updatedUrls = currentUrls.filter((_, idx) => idx !== removedIndex)
          setUrlFieldValue(updatedUrls)
        }

        // Remove from list
        return prev.filter(item => item.file.name !== file.name)
      })
    },
    [getUrlFieldValues, setUrlFieldValue],
  )

  const resetFileState = useCallback(() => {
    setFilesToUpload([])
    secureFileUpload.resetAllUploadState()

    // Clear form URLs if setter is provided
    if (setUrlFieldValue) {
      setUrlFieldValue([])
    }
  }, [setUrlFieldValue, secureFileUpload])

  const updateFileState = useCallback(
    (filePaths = []) => {
      // Update form URLs if setter is provided
      if (setUrlFieldValue) {
        setUrlFieldValue(filePaths)
      }

      // Convert file paths to file objects for display
      const fileObjects = filePaths.map(filePath => ({
        file: {
          name: formatFileName(filePath),
          url: filePath,
        },
        uploaded: true,
      }))
      setFilesToUpload(fileObjects)
    },
    [setUrlFieldValue],
  )

  return {
    // State
    filesToUpload,
    uploadedFiles: filesToUpload.filter(item => item.uploaded).map(item => item.file),
    pendingFiles: filesToUpload.filter(item => !item.uploaded).map(item => item.file),
    // Handlers
    handleFileSelect,
    handleFileRemove,
    resetFileState,
    updateFileState,
    // Secure upload instance
    secureFileUpload,
  }
}

export default useRequestFormFileUpload
